// DOM-free core of the live OGS-streaming pipeline (STREAM_PIPELINE.md).
//
// This is the reusable DOM-free engine: WebSocket + decode worker ingest, the
// bounded decode→render jitter buffer, vertstride lerp, and the per-frame
// untile→lift→SH apply. It owns NO canvas, camera, controls, or DOM — the host
// supplies a THREE renderer + drives `update(cameraPos)` from its own render
// loop, and adds `generators` to its own scene.
//
// Host: StreamAvatarModel.jsx — the in-world R3F avatar (shared scene
// the scene's splat mesh), mounted from App.jsx (Switch Character).
//
// The server runs the CNN/SH inference; the browser only lifts UV→3D Gaussians
// and re-evaluates SH color view-dependently each render frame. NO in-browser CNN.

import type * as THREE from "three";

export interface StreamStats {
  renderFps: number;
  decodeFps: number;
  branchLabel: string;
  degree: number;
  queueLen: number;
}

export interface StreamControllerOpts {
  /** THREE renderer the untile + lift passes run on (the host's, NOT a private one). */
  renderer: THREE.WebGLRenderer;
  /** Fully-built WS URL (already carries ?character/bitrate/vertstride). */
  wsUrl: string;
  /** Resolve the scene (scene.json URL) the client lifts against, given the
   *  server meta. The mesh topology MUST match the streamed character. The URL
   *  is the character's server-side `/ogs` scene row. */
  resolveSceneUrl: (meta: any) => string;
  /** Build-progress line from the server, sent while it loads a character that was
   *  not resident. Drives the host's loading overlay — a cold TensorRT compile is
   *  minutes, and an unannounced wait is indistinguishable from a hang. */
  onStage?: (stage: string) => void;
  /** Map a scene sub-asset filename → its server URL (per-file AAM rows).
   *  Required — streaming assets are server-sourced only, no bundled fallback. */
  resolveAssetUrl: (filename: string) => string;
  /** Fetch one scene asset. Omit for plain `fetch`; the host passes
   *  `cachedFetchAamFile` so the stream shares the on-device /ogs path's URL
   *  policy + Cache Storage instead of re-downloading meshes every load. */
  fetchAsset?: (url: string, size?: number) => Promise<Response>;
  /** Fetch the scene.json descriptor. Kept separate from `fetchAsset` so the host
   *  can leave it UNCACHED — a same-size descriptor edit would slip past the
   *  cache's size-versioned key and shadow the server. */
  fetchDescriptor?: (url: string, size?: number) => Promise<Response>;
  /** Byte size by filename, for the cache's size-versioned key. */
  sizeOf?: (filename: string) => number | undefined;
  /** decode→render FIFO depth; 0 = latest-wins oracle (no playout). Default 3. */
  queueDepth?: number;
  /** send head verts every Nth frame, client lerps the gaps. Default 1. */
  vertStride?: number;
  onStatus?: (m: string) => void;
  onStats?: (s: StreamStats) => void;
}

export interface StreamController {
  /** Per-stream metadata from the server (fps, look_at, radius, fov, rig_*…). */
  meta: any;
  /** The resolved scene URL the lift runs against (so the host can locate rig_names.json). */
  sceneUrl: string;
  /** True when the server runs live inference and accepts rig updates. */
  rigDriven: boolean;
  /** Splat objects to add to the host scene: [head] or [head, body]. */
  generators: THREE.Object3D[];
  /** Send a head control-rig to the server (live inference). No-op if not rig-driven. */
  sendRig(rig: ArrayLike<number>): void;
  /** Call once per host render frame with the live camera world position. Drains the
   *  decode→render queue (untile+lift on a new frame) and re-shades SH from the camera. */
  update(cameraPos: THREE.Vector3): void;
  dispose(): void;
}

export async function createStreamController(opts: StreamControllerOpts): Promise<StreamController> {
  // STREAMING IS OFF UNTIL ITS LIFT IS PORTED, AND IT REFUSES BEFORE IT COSTS
  // ANYTHING. `StreamLiftGenerator` implemented the UV->3D lift as a SPARK DYNO
  // — a GPU program in Spark's own shader DSL — and Spark is gone, so there is
  // nothing to run it. It is the one thing in the migration that was a real port
  // rather than a swap.
  //
  // THE REFUSAL IS THE FIRST STATEMENT, and that placement is the fix rather than
  // a tidy-up. It used to sit where the lift was constructed, which is AFTER the
  // decode worker was spawned and the WebSocket opened — so every `?stream=1`
  // load made the server load a bundle and compile its TensorRT engines, MINUTES
  // of GPU work on a shared box, before the browser threw it away. The worker was
  // never terminated either, so each attempt leaked one. Nothing here may run
  // before the throw.
  //
  // It is NOT ported blind, and the reason is specific: the streaming lift was
  // ALREADY BROKEN before Spark was removed. Measured on convorcher-staging with
  // one flag between the arms (`?character=isaac-v6&stream=0|1`, same bundle,
  // same session), the on-device path renders a full correct head and the
  // streaming path renders the eye/teeth region only — with a clean console on
  // both. It was bisected to the SERVER's vertices for the head branch sitting
  // ~45 cm below the body's (head median y -3.80 against +41.35 at the bundle's
  // world_scale 0.01), i.e. its Kabsch calibration, which is in this repo's
  // `server/`, not in the browser. So a TSL port has no reference worth matching:
  // diffing against that arm cannot tell "my port is wrong" from "my port
  // faithfully reproduces a broken reference". Fix the server half first.
  //
  // The port target is a TSL COMPUTE node, not WGSL: compute runs on both
  // backends (`WebGLBackend.compute()` uses transform feedback under
  // RASTERIZER_DISCARD), so one description covers the WebGPU default and the
  // WebGL fallback. `GaussianSplat`'s own spherical-harmonics pass is already a
  // TSL compute node doing exactly this. Everything the port still needs is
  // intact and untouched: the decode worker and its WS protocol, the untile
  // passes, the jitter buffer. What is deleted is only the body of THIS function
  // — the wiring between them, which named `StreamLiftGenerator` and could not
  // have run. `git log` has the deleted implementation.
  void opts;
  throw new Error(
    "[stream] the in-world streaming lift is not available: it was implemented against a splat "
    + "renderer this app no longer uses. It needs a TSL compute port — and the server's "
    + "head-branch calibration has to be fixed first, or there is no correct reference to port "
    + "against (see the note above this throw). Drop ?stream=1 to run on-device.",
  );
}
